import { Card, Tooltip } from "@mui/material"
import MDBox from "components/MDBox"
import React, { useEffect } from "react"

import { ReactComponent as Letter } from "assets/images/icons/letter.svg"
import { ReactComponent as Phone } from "assets/images/icons/phone.svg"

import InfoIcon, { TooltipIconColor, TooltipType } from "components/InfoIcon"
import { teacherInfoIconBlock } from "components/InfoIcon/teacherInfoIcon"
import makeStyles from "pages/style"

import "./style.css"
import { lxoSpace, lxoRadius, lxoSurface } from "shared/styles/lxoDashboardTokens"
import MDTypography from "components/MDTypography"
import { headerStyle } from "components/Category/style"
import { selectAuth } from "shared/redux/slices/authSlice"

import {
  useLazyGetAllTeamLXOQuery,
  useLazyGetDetailLXOMyTeamQuery,
} from "shared/redux/endpoints/dashboardMytaskLXO"
import { useSelector } from "react-redux"

// Generate initials from a full name string
const getInitials = (fullName = "") => {
  const parts = fullName.trim().split(/\s+/)
  if (parts.length === 0 || !parts[0]) return "?"
  if (parts.length === 1) return parts[0][0].toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

const MyTeamLXO = ({ activeTeam, setActiveTeam }) => {
  const auth = useSelector(selectAuth)
  const centerId = auth?.centerId
  const PAGE_SIZE = 8
  const [users, setUsers] = React.useState([])
  const [page, setPage] = React.useState(0)
  const [hasMore, setHasMore] = React.useState(false)
  const [isLoadingMore, setIsLoadingMore] = React.useState(false)
  const listRef = React.useRef(null)
  const classes = makeStyles()
  const [triggerAllTeamLXO, { data: dataAllTeamLXO }] =
    useLazyGetAllTeamLXOQuery()
  const listTeam = dataAllTeamLXO?.data || []

  const [triggerDetailLXOMyTeam] = useLazyGetDetailLXOMyTeamQuery()

  useEffect(() => {
    triggerAllTeamLXO({
      size: 8,
      page: 0,
      centerId,
    }).then((result) => {
      const firstTeamId = result?.data?.data?.[0]?.teamId
      if (firstTeamId) {
        setActiveTeam(firstTeamId)
      }
    })
  }, [centerId])

  const extractItems = (payload) => {
    if (!payload) return []
    if (Array.isArray(payload)) return payload
    if (Array.isArray(payload?.data)) return payload.data
    return []
  }

  const computeHasMore = (payload, itemsLength, totalLoaded, nextPage) => {
    const total =
      payload?.total ?? payload?.totalElements ?? payload?.totalCount
    const totalPages = payload?.totalPages
    const size = payload?.size ?? PAGE_SIZE
    const pageIndex =
      payload?.page ?? payload?.pageNumber ?? Math.max(nextPage, 0)

    if (typeof totalPages === "number") {
      return pageIndex + 1 < totalPages
    }
    if (typeof total === "number") {
      return totalLoaded < total
    }
    return itemsLength === size
  }

  const fetchTeamMembers = React.useCallback(
    async (nextPage, append = false) => {
      if (!activeTeam) return
      setIsLoadingMore(true)
      const res = await triggerDetailLXOMyTeam({
        teamId: activeTeam,
        centerId,
        size: PAGE_SIZE,
        page: nextPage,
      })
      const payload = res?.data?.data
      const items = extractItems(payload)
      setUsers((prev) => {
        const merged = append ? [...prev, ...items] : items
        setHasMore(
          computeHasMore(payload, items.length, merged.length, nextPage)
        )
        return merged
      })
      setPage(nextPage)
      setIsLoadingMore(false)
    },
    [activeTeam, centerId, triggerDetailLXOMyTeam]
  )

  useEffect(() => {
    if (!activeTeam && listTeam.length > 0) {
      setActiveTeam(listTeam[0]?.teamId || null)
      return
    }
    if (activeTeam) {
      setUsers([])
      setPage(0)
      setHasMore(false)
      fetchTeamMembers(0, false)
    }
  }, [centerId, activeTeam, listTeam.length, fetchTeamMembers])

  const handleLoadMore = () => {
    if (isLoadingMore || !hasMore) return
    fetchTeamMembers(page + 1, true)
  }

  const handleScroll = (e) => {
    if (isLoadingMore || !hasMore) return
    const el = e.currentTarget
    const threshold = 80
    const reachedBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight <= threshold
    if (reachedBottom) handleLoadMore()
  }

  return (
    <Card
      sx={{
        padding: `${lxoSpace.lg}px ${lxoSpace.xl}px`,
        background: lxoSurface.background,
        border: lxoSurface.border,
        borderRadius: `${lxoRadius.surface}px !important`,
        boxShadow: lxoSurface.shadow,
        "@media (max-width: 1600px)": {
          height: "100%",
        },
      }}
      className="my-team-card"
    >
      {/* Header: title + team selector */}
      <MDBox display="flex" alignItems="center" mb={1.5} gap={1.5}>
        <InfoIcon
          type={TooltipType.TYPE_2}
          teacherInfoIconBlock={teacherInfoIconBlock.WORK_STATION}
          color={TooltipIconColor.BLUE}
          className={classes.containerInfoIconWith2}
        >
          <MDTypography
            variant="h5"
            sx={(theme) => ({
              ...headerStyle(theme, { index: 0 }),
              color: "#0F172A",
              fontWeight: 700,
              fontSize: "17px !important",
              lineHeight: "1.35",
              letterSpacing: "-0.01em",
              whiteSpace: "nowrap",
              width: "auto",
              padding: 0,
              marginBottom: 0,
              [theme.breakpoints.down(1600)]: {
                fontSize: "17px !important",
                padding: "0",
                marginBottom: "0",
              },
            })}
          >
            My Team
          </MDTypography>
        </InfoIcon>

        <MDBox
          display="flex"
          alignItems="center"
          gap={0.75}
          className="list-team"
        >
          {listTeam.map((team, index) => (
            <Tooltip
              key={index}
              title={team?.teamName || ""}
              arrow
              placement="top"
            >
              <MDTypography
                className={`info-team ${
                  activeTeam === team.teamId ? "active" : ""
                }`}
                onClick={() => setActiveTeam(team?.teamId)}
              >
                {team?.teamName}
              </MDTypography>
            </Tooltip>
          ))}
        </MDBox>
      </MDBox>

      {/* User list */}
      <MDBox className="user-table">
        <MDBox
          className="user-table__body"
          onScroll={handleScroll}
          ref={listRef}
        >
          {users.length > 0 ? (
            users.map((u, index) => (
              <MDBox
                className={`row ${index === 0 ? "active" : ""}`}
                key={index}
              >
                {/* Avatar initials */}
                <div className="user-avatar">{getInitials(u.fullName)}</div>
                {/* Name */}
                <MDBox flex={1} className="user">
                  <Tooltip
                    title={u.fullName || ""}
                    arrow
                    placement="left-start"
                  >
                    <span className="user__name">
                      {u.fullName}{" "}
                      {u.isManager && (
                        <div className="user__manager">Manager</div>
                      )}
                    </span>
                  </Tooltip>
                  {u.isBirthday && (
                    <div className="user__birthday">
                      <Tooltip title="Happy Birthday" arrow placement="top">
                        🎉
                      </Tooltip>
                    </div>
                  )}
                </MDBox>

                {/* Role badge */}
                <Tooltip
                  title={u.userTitles || ""}
                  arrow
                  placement="left-start"
                >
                  <MDBox className="role-badge">{u.userTitles}</MDBox>
                </Tooltip>

                {/* Contact buttons */}
                <MDBox className="contacts">
                  <Tooltip title={u.email || ""} arrow placement="top">
                    <button className="icon-btn" title="Email">
                      <a href={`mailto:${u.email}`}>
                        <Letter />
                      </a>
                    </button>
                  </Tooltip>

                  <Tooltip title={u.phoneNumber || ""} arrow placement="top">
                    <button className="icon-btn" title="Phone">
                      <a href={`tel:${u?.phoneNumber}`}>
                        <Phone />
                      </a>
                    </button>
                  </Tooltip>
                </MDBox>
              </MDBox>
            ))
          ) : (
            <MDBox className="row" p={1}>
              <MDTypography className="user__name" sx={{ color: "#8C9BAB" }}>
                No data
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      </MDBox>
    </Card>
  )
}

export default MyTeamLXO
